import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getNotifications, markAllNotificationsRead, markNotificationRead } from "../utils/api.js";
import {
  browserNotifyPermission,
  getLastSeenNotificationId,
  requestBrowserNotify,
  setLastSeenNotificationId,
  showBrowserNotify,
} from "../utils/browserNotify.js";

const POLL_MS = 15000;

const PATHS = {
  milk_week: "/providers",
  milk_day: "/providers",
  cheese_week: "/cheeses",
  cheese_day: "/cheeses",
};

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const navigate = useNavigate();
  const lastSeenRef = useRef(getLastSeenNotificationId());
  const primedRef = useRef(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState(browserNotifyPermission);

  const announceNew = useCallback(
    (fresh) => {
      fresh.forEach((item) => {
        const shown = showBrowserNotify(item.title, item.body, {
          onClick: () => navigate(PATHS[item.type] || "/"),
        });
        if (!shown) {
          toast(item.title, { position: "top-right" });
        }
      });
    },
    [navigate],
  );

  const fetchInbox = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const res = await getNotifications({ page: 1, limit: 20 });
        const next = res.data.data || [];
        const nextUnread = res.data.unread || 0;
        setItems(next);
        setUnread(nextUnread);

        const newestId = next[0]?._id ? String(next[0]._id) : "";
        if (!primedRef.current) {
          primedRef.current = true;
          if (newestId) {
            lastSeenRef.current = newestId;
            setLastSeenNotificationId(newestId);
          }
        } else if (newestId && newestId !== lastSeenRef.current) {
          const fresh = [];
          for (const item of next) {
            if (String(item._id) === lastSeenRef.current) break;
            fresh.push(item);
          }
          lastSeenRef.current = newestId;
          setLastSeenNotificationId(newestId);
          if (fresh.length) announceNew(fresh);
        }
      } catch (err) {
        if (!silent) {
          toast.error(err.response?.data?.error || "No se pudieron cargar las notificaciones", {
            position: "top-center",
          });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [announceNew],
  );

  useEffect(() => {
    fetchInbox({ silent: true });
    const timer = setInterval(() => fetchInbox({ silent: true }), POLL_MS);
    const onRefresh = () => fetchInbox({ silent: true });
    window.addEventListener("notifications:refresh", onRefresh);
    window.addEventListener("focus", onRefresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener("notifications:refresh", onRefresh);
      window.removeEventListener("focus", onRefresh);
    };
  }, [fetchInbox]);

  const enableBrowser = useCallback(async () => {
    const next = await requestBrowserNotify();
    setPermission(next);
    if (next === "granted") {
      toast.success("Avisos del navegador activados", { position: "top-center" });
    } else if (next === "denied") {
      toast.error("El navegador bloqueó los avisos. Actívalos en la configuración del sitio.", {
        position: "top-center",
      });
    }
    return next;
  }, []);

  const markRead = useCallback(async (id) => {
    await markNotificationRead(id);
    setItems((prev) => prev.map((row) => (row._id === id ? { ...row, read: true } : row)));
    setUnread((n) => Math.max(0, n - 1));
  }, []);

  const markAllRead = useCallback(async () => {
    await markAllNotificationsRead();
    setItems((prev) => prev.map((row) => ({ ...row, read: true })));
    setUnread(0);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        items,
        unread,
        loading,
        permission,
        fetchInbox,
        enableBrowser,
        markRead,
        markAllRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotifications debe usarse dentro de NotificationProvider");
  }
  return ctx;
};
